import React, { CSSProperties, useEffect, useRef } from "react";
import { fromEvent } from "rxjs";
import { CpTooltip } from "@components";
import { hasSortOrFilter } from "./column.helper";
import { CpDateRangeSelector } from "./menu-content.component";

import type {
  Column,
  FilterCallResult,
  FilterContext,
  FilterContextFilters,
  FilterValue,
} from "./cp-table-header-menu.types";

import styles from "./cp-table-header-menu.styles.css";

// TODO: Type this properly - extended Event with custom property
type ExtendedMouseEvent = MouseEvent & {
  clickedOnFourByThree?: boolean;
};

export type CpTableHeaderMenuProps = {
  filterContext: FilterContext;
  column: Column;
  getDynamicFilters?: (
    filterName: string,
    columnFilters: FilterContextFilters,
    sortFilterList?: boolean,
    filterSearchValue?: string | false,
  ) => FilterCallResult;
  getFixedFilters?: (column: Column) => FilterValue[];
  allowFilterSort?: boolean;
  hideSortOptions?: boolean;
  onCancel?: () => void;
  menuShown?: boolean;
  setColumnMenuShown: (shown: boolean) => void;
};

export const CpTableHeaderMenu = ({
  filterContext,
  column,
  getDynamicFilters,
  getFixedFilters,
  allowFilterSort,
  hideSortOptions,
  onCancel,
  menuShown,
  setColumnMenuShown,
}: CpTableHeaderMenuProps) => {
  // TODO: Type this properly - original code accesses ref without .current
  const columnHeaderMenu = useRef<any>() as any;

  useEffect(() => {
    const clickSubscription = fromEvent(document, "click").subscribe((e) => {
      handlePageClick(e as ExtendedMouseEvent);
    });

    return () => clickSubscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    filterContext.applyFilter(column.sortParam, {});
    setColumnMenuShown(false);
  };

  const handlePageClick = (e: ExtendedMouseEvent) => {
    if (
      columnHeaderMenu &&
      menuShown &&
      !e.clickedOnFourByThree &&
      !columnHeaderMenu.contains(e.target) &&
      !columnHeaderMenu.parentElement.contains(e.target) // check the th element as well
    ) {
      setColumnMenuShown(false);
    }
  };

  const columnFilter = allowFilterSort
    ? filterContext.filters[column.sortParam]
    : null;

  const menuStyles: CSSProperties = {
    cursor: "default",
    color: "black",
    minWidth: "224px",
    top: "calc(100% + 8px)",
    left: "-12px",
    fontWeight: 400,
    overflow: "hidden",
  };

  if (!!column.menuAlignRight) {
    menuStyles.left = "auto";
    menuStyles.right = "10px";
  } else if (column.columnLabel === "Due Date") {
    menuStyles.left = "-190px";
  }

  return (
    <div
      ref={columnHeaderMenu}
      className={`cps-inline-block cps-animate-fade`}
      style={{ width: "100%" }}
    >
      <div
        className={`cps-dropdown ${menuShown ? "cps-open" : ""} ${
          styles.spreadContents
        } ${styles.columnHeaderText}`}
      >
        <div className={column.columnHeaderClass}>
          <span>{column.columnLabel}</span>
          {allowFilterSort && (
            <i
              className={`cps-icon cps-icon-sm-caret-${
                columnFilter?.order === "asc" && column?.filterType === "none"
                  ? "up"
                  : "down"
              } ${styles.centerIcon}`}
            />
          )}
        </div>
        {allowFilterSort && hasSortOrFilter(columnFilter) && (
          <CpTooltip text="Clear sort and filter">
            <div className="cps-btn-icon" onClick={resetFilters}>
              <a className="cps-link" style={{ marginTop: 0 }}>
                <span className="cps-icon cps-icon-sm-neg" />
              </a>
            </div>
          </CpTooltip>
        )}
        {menuShown && (
          <CpDateRangeSelector
            filterContext={filterContext}
            column={column}
            getDynamicFilters={getDynamicFilters}
            getFixedFilters={getFixedFilters}
            hideSortOptions={hideSortOptions}
            onCancel={onCancel}
            setColumnMenuShown={setColumnMenuShown}
            menuStyles={menuStyles}
          />
        )}
      </div>
    </div>
  );
};
